import React from 'react';
import { EventContentArg } from '@fullcalendar/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock } from '@fortawesome/pro-regular-svg-icons';
import { faPaw } from '@fortawesome/pro-light-svg-icons';
import { createStyles, makeStyles, Theme } from '@material-ui/core';
import { SlotGroup } from 'components/Calendar3/FullCalendar/model';
import Appointment from '@spike/appointment-model';

export type EventSlotGroupFullCalendarProps = EventContentArg & {
    appointments: Array<Appointment>;
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            fontSize: 12,
            color: '#000',
            fontWeight: 500,
            borderRadius: 6,
            margin: '2px 0px',
            padding: '2px 4px',
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            justifyContent: 'space-between',
            backgroundColor: '#EFEFEF',

            [theme.breakpoints.up('lg')]: {
                fontSize: 14,
                margin: '3px 0px',
                padding: '6px 5px'
            }
        },
        petsCountBadge: {
            'gap': 2,
            'fontSize': 12,
            'lineHeight': 1,
            'fontWeight': 600,
            'color': '#ffffff',
            'borderRadius': 3.5,
            'padding': '4.5px 5px',
            'display': 'inline-flex',
            'alignItems': 'center',
            'backgroundColor': '#000000',

            [theme.breakpoints.up('md')]: {
                fontSize: 14
            },

            '& svg': {
                marginRight: 4
            }
        }
    })
);

export const EventSlotGroupFullCalendar: React.FC<EventSlotGroupFullCalendarProps> = props => {
    const classes = useStyles();

    const appointmentsCount = React.useMemo(() => {
        const staffAppointments = props.appointments
            // filter by staff id
            .filter(appointment =>
                appointment.services.some(service =>
                    props.event.extendedProps.slotGroup.staffSlots.some(
                        (staffSlot: SlotGroup['staffSlots'][number]) => staffSlot.staff.id === service.staff?.id
                    )
                )
            )
            // filter by date and time
            .filter(appointment =>
                appointment.duration.from.isSame(props.event.extendedProps.slotGroup.date, 'minute')
            );

        return staffAppointments.length;
    }, [props.appointments, props.event.extendedProps.from, props.event.extendedProps.slotGroup]);

    const slotGroupPetsCount = React.useMemo(() => {
        return props.event.extendedProps.slotGroup.staffSlots.reduce(
            (acc: number, staffSlot: SlotGroup['staffSlots'][number]) => acc + staffSlot.slot.petsCount,
            0
        );
    }, [props.event.extendedProps.slotGroup]);

    return (
        <div className={classes.root}>
            <span>
                <FontAwesomeIcon icon={faClock} /> {props.event.extendedProps.slotGroup.date.format('h:mm A')}
            </span>

            <span className={classes.petsCountBadge}>
                <FontAwesomeIcon icon={faPaw} />
                {appointmentsCount}
                {' / '}
                {slotGroupPetsCount}
            </span>
        </div>
    );
};
